import { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../context/AuthContext"; // Import AuthContext to manage authentication
import { useNavigate, Link } from "react-router-dom";
import CreditBalanceBadge from "./CreditBalanceBadge";
import { FaSun, FaMoon } from "react-icons/fa";

const Header = ({ toggleSidebar }) => {
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem("darkMode");
    return savedMode ? JSON.parse(savedMode) : false;
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [accountSettingsPath, setAccountSettingsPath] = useState("");
  const navigate = useNavigate();
  const dropdownRef = useRef(null); // Ref for the dropdown menu

  useEffect(() => {
    setAccountSettingsPath(
      user.role === "admin" ? "/admin/account-settings" : "/account-settings"
    );

    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    localStorage.setItem("darkMode", JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode((prevMode) => !prevMode);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    logout(navigate);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false); // Close the dropdown if clicked outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="py-4 px-6 dark:bg-gray-900 dark:text-white dark:border-b-4 dark:border-white-100 flex justify-between items-center border-b-4 border-indigo-600 dark:border-gray-500">
      <button
        onClick={toggleSidebar}
        className="dark:text-white text-lg block md:hidden"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          className="size-7"
        >
          <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
        </svg>
      </button>

      <div className="flex items-center space-x-4 ml-auto">
        <CreditBalanceBadge balance={user?.credit_balance || 0} />
        <div className="flex items-center space-x-2">
          <button
            onClick={toggleDarkMode}
            className="focus:outline-none p-2 rounded-full bg-gray-200 dark:bg-gray-700 transition-colors"
          >
            {isDarkMode ? (
              <FaSun className="text-yellow-400 w-5 h-5" />
            ) : (
              <FaMoon className="text-gray-800 dark:text-gray-200 w-5 h-5" />
            )}
          </button>
        </div>

        {/* User Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={toggleDropdown}
            className="focus:outline-none flex items-center space-x-2"
          >
            <img
              src="https://via.placeholder.com/32"
              alt="User Avatar"
              className="w-8 h-8 rounded-full"
            />
            <span className="text-sm font-medium">
              <strong>
                {isAuthenticated ? user && user.username : "Guest"}
              </strong>
            </span>
            <svg
              className={`w-4 h-4 transform transition-transform ${
                isDropdownOpen ? "rotate-180" : "rotate-0"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-lg rounded-lg z-10">
              {isAuthenticated && (
                <>
                  <Link
                    to={accountSettingsPath}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    Account Settings
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-red-500 hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
